import { useCallback, useMemo, useState } from 'react';

import { IIntlMessage } from '../_core/IntlMsg/intl-msg.types';
import { readErrorExtension, readSendIncomplete, readUserInputMessage } from '../../lib/helpers/graphql-error.helpers';
import { parentReportCardPath } from '../../lib/helpers/parent-links.helpers';
import { useIntlSnackbar } from '../../lib/hooks/use-intl-snackbar';
import { useOperationErrorHandler } from '../../lib/hooks/use-operation-error-handler';
import { createSingleFlightScope, useSingleFlight } from '../../lib/hooks/use-single-flight';
import {
  buildExtendReportCardLinks,
  buildSendReportCards,
  buildSetPlayerReportCardAccess,
  useReportCardSurfaceResolver,
} from '../../lib/services/report-card-access.service';
import { OUTCOME_SNACKBAR_MS } from '../../lib/snackbar.constants';
import { useCompanySportTerminology } from '../../lib/stores/company.store';
import { getPlayerName } from '../../lib/utils/player-name.service';
import { playerResendReport, playerSendReport, reportCardWriteFailures } from './report-card-access.helpers';
import { getMessages } from './report-card-access.messages';
import { ConfirmedReportCardWrite, IContactLink, ISendReport, ReportCardWrite } from './report-card-access.types';

// One write per player at a time, across every surface that shows the player's report card actions.
const REPORT_CARD_WRITES = createSingleFlightScope();

type Succeed = (message: IIntlMessage, variant?: ISendReport['variant'], values?: ISendReport['values']) => void;

export interface IReportCardConfirming {
  action: ConfirmedReportCardWrite;
  playerAssessment: IPlayerAssessment;
}

export const reportCardPlayerName = (playerAssessment: IPlayerAssessment): string =>
  getPlayerName({ ...playerAssessment, firstNameFirst: true }).trim();

// The report card writes for every player on a table or page, built once. Each write is confirmed
// first (except extending links), runs single-flight per player and reports its outcome.
export const useReportCardActions = () => {
  const termLookup = useCompanySportTerminology();
  const messages = useMemo(() => getMessages(termLookup), [termLookup]);
  const failures = useMemo(() => reportCardWriteFailures(messages), [messages]);
  const showSnackbar = useIntlSnackbar();
  const reportFailure = useOperationErrorHandler(messages.errLinkNotCopied);
  const surfaceFor = useReportCardSurfaceResolver();

  const { sendReportCards } = buildSendReportCards();
  const { setPlayerReportCardAccess } = buildSetPlayerReportCardAccess();
  const { extendReportCardLinks } = buildExtendReportCardLinks();

  const [confirming, setConfirming] = useState<IReportCardConfirming | null>(null);

  // What one player's send or resend did, from the server's own result. A send that reached only some
  // contacts rejects instead, and the server retries the failed ones.
  const sendToPlayer = useCallback(async (playerAssessment: IPlayerAssessment, resend: boolean): Promise<ISendReport> => {
    const { _id: playerAgeGroupId, assessmentId } = playerAssessment.ageGroup;
    try {
      const result = await sendReportCards({
        assessmentId, ageGroupIds: [playerAgeGroupId], playerAssessmentIds: [playerAssessment._id], resend,
      });
      return resend ? playerResendReport(messages, result) : playerSendReport(messages, result);
    } catch (error) {
      if (!readSendIncomplete(error)) throw error;
      console.warn(`report card ${resend ? 'RESEND' : 'SEND'} reached only some contacts:`, error);
      return { message: resend ? messages.msgResendIncomplete : messages.msgSendIncomplete, variant: 'warning' };
    }
  }, [messages, sendReportCards]);

  const write = useCallback(async (action: ReportCardWrite, playerAssessment: IPlayerAssessment) => {
    const values = { name: reportCardPlayerName(playerAssessment) };
    const succeed: Succeed = (message, variant = 'success', extra = {}) =>
      showSnackbar(message, variant, OUTCOME_SNACKBAR_MS, { ...values, ...extra });

    try {
      if (action === 'SEND' || action === 'RESEND') {
        const { message, variant, values: counts } = await sendToPlayer(playerAssessment, action === 'RESEND');
        return succeed(message, variant, counts);
      }
      if (action === 'EXTEND_LINKS') {
        const { data } = await extendReportCardLinks(playerAssessment._id);
        // False when every link had already expired: expired links stay expired, and a resend issues new ones.
        if (data?.refreshReportCardAccessTokens === false) return succeed(messages.msgNoLinksToExtend, 'warning');
        return succeed(messages.msgLinksExtended);
      }

      const enabled = action === 'RE_ENABLE';
      const { data } = await setPlayerReportCardAccess(playerAssessment, enabled);
      const notReleased = data?.setPlayerReportCardAccess.notReleasedPlayerAssessmentIds.includes(playerAssessment._id);
      if (enabled && notReleased) return succeed(messages.msgReEnabledNotSent);
      return succeed(enabled ? messages.msgReEnabled : messages.msgRevoked);
    } catch (error) {
      // A resend waits while the last report card is still out; the server says which state it is in.
      const delivery = action === 'RESEND' ? readErrorExtension<string>(error, 'reportCardDelivery') : null;
      if (delivery) {
        const message = delivery === 'RETRYING' ? messages.errResendRetrying : messages.errResendSending;
        return reportFailure(`report card ${action}`, error, { message, values });
      }
      const reason = readUserInputMessage(error);
      const { failed, refused } = failures[action];
      return reportFailure(`report card ${action}`, error, { message: reason ? refused : failed, values: { ...values, reason } });
    }
  }, [messages, failures, showSnackbar, reportFailure, sendToPlayer, extendReportCardLinks, setPlayerReportCardAccess]);

  const { run, isInFlight } = useSingleFlight(write, {
    scope: REPORT_CARD_WRITES,
    key: (action, playerAssessment) => playerAssessment._id,
  });

  const request = useCallback((action: ReportCardWrite, playerAssessment: IPlayerAssessment) => {
    if (action === 'EXTEND_LINKS') return run(action, playerAssessment);
    setConfirming({ action, playerAssessment });
    return undefined;
  }, [run]);

  const confirm = useCallback(() => {
    if (!confirming) return;
    setConfirming(null);
    run(confirming.action, confirming.playerAssessment);
  }, [confirming, run]);

  const cancel = useCallback(() => setConfirming(null), []);

  const copyLink = useCallback((playerAssessment: IPlayerAssessment, { contact, token }: IContactLink) => {
    const link = `${window.location.origin}${parentReportCardPath(playerAssessment.player.code, playerAssessment._id, token)}`;
    return navigator.clipboard.writeText(link)
      .then(() => showSnackbar(messages.msgLinkCopied, 'success', OUTCOME_SNACKBAR_MS, { contact: contact.name || contact.email }))
      .catch(error => reportFailure('copy report card link', error));
  }, [messages, showSnackbar, reportFailure]);

  const view = useCallback((playerAssessment: IPlayerAssessment) => {
    window.open(`/report-card/${playerAssessment.player.code}/${playerAssessment._id}`, '_blank');
  }, []);

  // Memoized: the provider builds the rows' context from these, so it changes only when one of them does.
  return useMemo(() => ({
    messages, surfaceFor, isInFlight, confirming, request, confirm, cancel, copyLink, view,
  }), [messages, surfaceFor, isInFlight, confirming, request, confirm, cancel, copyLink, view]);
};

export type ReportCardActionsApi = ReturnType<typeof useReportCardActions>;
